import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, videosByCategory } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'drama');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const videos = videosByCategory[activeCategory] || [];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - BaoyuTV`,
    description: currentCategory?.description || '',
    url: `https://baoyutv.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'BaoyuTV',
      url: 'https://baoyutv.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory?.name || '内容分类'} - BaoyuTV高清视频平台`}
        description={currentCategory?.description || '浏览BaoyuTV海量视频内容分类，发现更多精彩。'}
        keywords={`baoyutv,${currentCategory?.name || ''},高清视频,在线观看`}
        canonicalUrl={`https://baoyutv.com/category/${activeCategory}`}
        ogTitle={`${currentCategory?.name || '内容分类'} - BaoyuTV高清视频平台`}
        ogDescription={currentCategory?.description || '浏览BaoyuTV海量视频内容分类，发现更多精彩。'}
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-64 md:pt-72 pb-10 relative overflow-hidden">
          <div className="absolute inset-0 top-0 h-full">
            {currentCategory && (
              <>
                <img
                  src={currentCategory.image}
                  alt={currentCategory.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
              </>
            )}
          </div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-accent-500/90 text-background-50 mb-4">
              <i className={`${currentCategory?.icon || 'ri-folders-line'} text-2xl`}></i>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
              {currentCategory?.name}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-lg mx-auto">
              {currentCategory?.description}
            </p>
          </div>
        </div>

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: currentCategory?.name || '分类' },
          ]}
        />

        <div className="w-full px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {videos.map((video, index) => (
                <div
                  key={index}
                  className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[2/3] overflow-hidden relative">
                    <img
                      src={video.cover}
                      alt={video.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="text-xs px-2 py-1 rounded-md bg-black/60 text-white backdrop-blur-sm">
                        {video.type}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="text-xs px-2 py-1 rounded-md bg-primary-500/90 text-background-50 font-semibold">
                        {video.rating}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground-900 text-sm mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <span className="text-xs text-foreground-400">{video.year}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}